import { Controller } from "@hotwired/stimulus"

// Handles two opt-in behaviours on the GoalieTask form panel:
//
//   1. last_day_of_month toggle — connects the "last_day_of_month" checkbox to
//      disable/enable the "new_billing_anchor_date" date field.
//      Requires targets: checkbox, dateField.
//
//   2. Contract value display — computes minimum × unit_price × months live and
//      updates a read-only display element. Reacts to changes in all four fields
//      including the currency selector so the correct symbol is shown.
//      Requires target: contractValueDisplay.
//      Input fields are located by name fragment rather than target so that generic
//      field types (:integer, :decimal, :currency) don't need task-specific markup.
export default class extends Controller {
  static targets = ["checkbox", "dateField", "contractValueDisplay"]

  connect() {
    if (this.hasCheckboxTarget) this.toggle()
    if (this.hasContractValueDisplayTarget) {
      this.#setupContractValueListeners()
      this.computeContractValue()
    }
  }

  toggle() {
    if (!this.hasDateFieldTarget) return
    const checked = this.checkboxTarget.checked
    this.dateFieldTarget.disabled = checked
    if (checked) {
      this.dateFieldTarget.value = ""
    }
  }

  computeContractValue() {
    const min = parseFloat(this.element.querySelector("[name*='[minimum]']")?.value) || 0
    const price = parseFloat(this.element.querySelector("[name*='[unit_price]']")?.value) || 0
    const monthsEl = this.element.querySelector("[name*='[months]']")
    // When no months field is rendered (e.g. UpgradeTask), fall back to DEFAULT_LENGTH_MONTHS (12).
    // When the field exists but is empty, keep 0 so the display shows "—" until the user enters a value.
    const months = monthsEl ? (parseInt(monthsEl.value, 10) || 0) : 12
    const currencyEl = this.element.querySelector("[name*='[currency]']")
    const currency = currencyEl?.value || "USD"
    const value = min * price * months
    this.contractValueDisplayTarget.textContent = value > 0
      ? value.toLocaleString("en-US", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "—"
  }

  #setupContractValueListeners() {
    ["minimum", "unit_price", "months", "currency"].forEach(field => {
      const el = this.element.querySelector(`[name*='[${field}]']`)
      if (el) el.addEventListener("input", () => this.computeContractValue())
    })
  }
}
